import React from "react";
///#region redux
import { useDispatch, useSelector } from "react-redux";
import { ThunkDispatch } from "redux-thunk";
import { AppState } from "store";
import { addHelp, addIntroduction } from "store/actions/authActions";
//#endregion
//#region types
import { TranslationKeys } from "translations";
import { CommonConstants, IconDefinitionConstants, FAQConstants } from "utils/constants";
import { DialogEnums, StyleEnums } from "models/enums";
//#endregion
//#region components
import { Button, Grid } from "@mui/material";
import { DialogContainer, Icon, StyledButton } from "components/common";
import { useUserService } from "serviceHooks";
//#endregion
import { LocalizationHelperInstance } from "utils/helpers";
import clsx from "clsx";
import cssExports from "theme/styles/main.module.scss";

const WelcomeGuideIntroductions: React.FC = () => {
  const userService = useUserService();
  const [isMinimized, setIsMinimized] = React.useState(false);
  const dispatch: ThunkDispatch<any, any, any> = useDispatch();
  const welcomeGuideId = CommonConstants.WelcomeGuideId;
  const [openIntroduction, setOpenIntroduction] = React.useState(false);

  const shownIntroductions = useSelector((state: AppState) =>
    state.auth.profileToggles.shownIntroductions?.includes(CommonConstants.WelcomeGuideId)
  );

  const shownHelps = useSelector((state: AppState) =>
    state.auth.profileToggles.shownHelps?.includes(CommonConstants.WelcomeGuideId)
  );

  React.useEffect(() => {
    setIntroductionStates();
  }, [shownIntroductions, shownHelps]);

  const setIntroductionStates = () => {
    if (shownIntroductions !== false && shownHelps !== false) {
      setOpenIntroduction(false);
    } else {
      if (shownIntroductions !== false) setIsMinimized(true);
      setOpenIntroduction(true);
    }
  };

  const onClose = () => {
    userService.markIntroductionAsShown(welcomeGuideId);
    userService.markHelpAsShown(welcomeGuideId);
    dispatch(addIntroduction(welcomeGuideId));
    dispatch(addHelp(welcomeGuideId));
  };

  const onGotItButtonClick = () => {
    userService.markHelpAsShown(welcomeGuideId);
    dispatch(addHelp(welcomeGuideId));
  };

  const onFAQButtonClick = () => {
    window.open(FAQConstants.FAQUrl, "_blank");
  };

  const onHowItWorksButtonClick = () => {
    userService.markIntroductionAsShown(welcomeGuideId);
    dispatch(addIntroduction(welcomeGuideId));
  };

  const getTitle = () => {
    if (!isMinimized) {
      return LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.FirstIntroductionTitle);
    } else {
      return LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.SecondIntroductionTitle);
    }
  };

  const getContent = () => {
    if (!isMinimized) {
      return (
        <>
          <Grid container>
            <Grid item className={cssExports.walkingManContent}>
              {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.FirstIntroductionContent)}
            </Grid>
            <Grid item>
              <b className={clsx(cssExports.boldContent)}>
                {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.ProTip).concat("!")}
              </b>{" "}
              {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.FirstIntroductionTip)}
            </Grid>
          </Grid>
          <Grid item>
            <StyledButton
              size="sm"
              className={cssExports.button}
              onClick={onHowItWorksButtonClick}
              isSubmitting={isMinimized}
            >
              {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.FirstIntroductionConfirmText)}
            </StyledButton>
          </Grid>
        </>
      );
    } else {
      return (
        <Grid container>
          <Grid item>
            {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.SecondIntroductionContent)}
          </Grid>
          <Grid item>
            <Button className={cssExports.faqButton} onClick={onFAQButtonClick}>
              {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.FaqButton)}
            </Button>
            <Icon
              icon={IconDefinitionConstants.WArrowSmallRight}
              className={clsx(cssExports.arrowButton, cssExports.faqButton)}
            />
            <StyledButton size="sm" className={cssExports.buttonProgress} onClick={onGotItButtonClick}>
              {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.GotItButton)}
            </StyledButton>
          </Grid>
        </Grid>
      );
    }
  };
  return (
    <>
      <DialogContainer
        isOpen={openIntroduction}
        dialogType={DialogEnums.DialogType.WalkingMan}
        type={StyleEnums.DisplayType.Info}
        isMinimized={isMinimized}
        title={getTitle()}
        onClose={onClose}
      >
        {getContent()}
      </DialogContainer>
    </>
  );
};

export default WelcomeGuideIntroductions;
